/**
 * PermissionGuard Component
 * Component สำหรับซ่อน/แสดง UI ตามสิทธิ์ของผู้ใช้
 */

import React from 'react';
import { usePermission } from '../../hooks/usePermission';

interface PermissionGuardProps {
  /**
   * สิทธิ์ที่ต้องการ (string หรือ array of strings)
   * ถ้าเป็น array จะเช็คว่ามีสิทธิ์อย่างน้อย 1 อัน (OR logic)
   */
  requires: string | string[];

  /**
   * ใช้ AND logic แทน OR (ต้องมีสิทธิ์ทั้งหมด)
   */
  requireAll?: boolean;

  /**
   * Element ที่จะแสดงถ้าไม่มีสิทธิ์ (default: ไม่แสดงอะไร)
   */
  fallback?: React.ReactNode;

  /**
   * Children elements
   */
  children: React.ReactNode;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  requires,
  requireAll = false,
  fallback = null,
  children,
}) => {
  const { hasPermission, hasAllPermissions, isSuperAdmin } = usePermission();

  // Super Admin มีสิทธิ์ทั้งหมด
  if (isSuperAdmin) {
    return <>{children}</>;
  }

  // ตรวจสอบสิทธิ์
  let hasAccess = false;

  if (Array.isArray(requires)) {
    if (requireAll) {
      // ต้องมีสิทธิ์ทั้งหมด
      hasAccess = hasAllPermissions(requires);
    } else {
      // มีสิทธิ์อย่างน้อย 1 อัน
      hasAccess = requires.some(p => hasPermission(p));
    }
  } else {
    hasAccess = hasPermission(requires);
  }

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

/**
 * Component wrapper สำหรับซ่อนปุ่ม Create
 */
export const CanCreate: React.FC<{ module: string; children: React.ReactNode; fallback?: React.ReactNode }> = ({
  module,
  children,
  fallback = null,
}) => {
  return (
    <PermissionGuard requires={`${module}.create`} fallback={fallback}>
      {children}
    </PermissionGuard>
  );
};

/**
 * Component wrapper สำหรับซ่อนปุ่ม Edit
 */
export const CanEdit: React.FC<{ module: string; children: React.ReactNode; fallback?: React.ReactNode }> = ({
  module,
  children,
  fallback = null,
}) => {
  return (
    <PermissionGuard requires={`${module}.edit`} fallback={fallback}>
      {children}
    </PermissionGuard>
  );
};

/**
 * Component wrapper สำหรับซ่อนปุ่ม Delete
 */
export const CanDelete: React.FC<{ module: string; children: React.ReactNode; fallback?: React.ReactNode }> = ({
  module,
  children,
  fallback = null,
}) => {
  return (
    <PermissionGuard requires={`${module}.delete`} fallback={fallback}>
      {children}
    </PermissionGuard>
  );
};

/**
 * Component wrapper สำหรับซ่อนปุ่ม View
 */
export const CanView: React.FC<{ module: string; children: React.ReactNode; fallback?: React.ReactNode }> = ({
  module,
  children,
  fallback = null,
}) => {
  return (
    <PermissionGuard requires={`${module}.view`} fallback={fallback}>
      {children}
    </PermissionGuard>
  );
};

export default PermissionGuard;
